import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";

import "./index.css";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Nada de la familia 4xx se reintenta. Son respuestas definitivas: el
      // servidor entendió la petición y dijo que no. Insistir tres veces no
      // cambia el veredicto, solo lo retrasa.
      //
      // Empezó siendo solo el 401 —sin sesión, insistir retrasa el redirect al
      // login—, y el 404 costó una pantalla en blanco: la ficha de un
      // movimiento borrado se pasaba tres reintentos con `isPending` en true,
      // pintando un Skeleton gris, y su «Ese movimiento no existe» no llegaba
      // a verse nunca. Se cazó vaciando la base y abriendo un enlace viejo.
      //
      // Los 5xx sí se reintentan: ahí el servidor no llegó a decidir.
      retry: (intentos, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return intentos < 2;
      },
      staleTime: 30_000,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const raiz = document.getElementById("app");
if (!raiz) throw new Error("Falta el div #app en index.html");

ReactDOM.createRoot(raiz).render(
  <StrictMode>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      disableTransitionOnChange
      storageKey="vite-ui-theme"
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster richColors position="top-right" />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
